import { useState, type CSSProperties } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Check, Copy, Eye, EyeOff, Info, Loader2 } from 'lucide-react'
import clsx from 'clsx'
import { getFirmApiKeyLocation } from '@/api/clients'
import { Tooltip } from '@/components/Tooltip'
import { MASKED_API_KEY_PLACEHOLDER } from '../overrideUtils'

export interface MaskedApiKeyFieldProps {
  value: string
  onChange: (value: string) => void
  /** New/copied firms render a plain required input from the start - there
   * is no prior real key to mask/replace (Core logic step 3). */
  isNewFirm: boolean
  /** Needed to call the key-location endpoint for an existing firm's masked view. */
  firmId: string
  error?: string
}

// Schema requires auth.api_key to be minLength 32 (configs/schemas/
// clients.schema.json) - a v4 UUID is 36 characters, comfortably valid, and
// already this repo's own convention for a client-generated random value
// (lookupUtils.ts's createLocalKey uses the same crypto.randomUUID()).
function generateApiKey(): string {
  return crypto.randomUUID()
}

// `-webkit-text-security: disc` masks the input's rendered text as dots
// while the input itself stays `type="text"` - deliberately NOT
// `type="password"`. A real `type="password"` input triggers Chrome's own
// "Update password?" save-password prompt on this admin tool (confirmed:
// the browser guesses a username/password pair even though this is an API
// key field, not a login form) - switching the value type would also submit
// a value the browser itself now thinks is a stored login credential. This
// CSS property is non-standard (missing from React's CSSProperties type,
// hence the `as CSSProperties` cast below) but supported by every
// Chromium/WebKit browser, which is what this app targets.
function maskedTextStyle(masked: boolean): CSSProperties {
  return { WebkitTextSecurity: masked ? 'disc' : 'none' } as CSSProperties
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard access can be denied (permissions, insecure context) -
      // nothing meaningful to recover into here, the operator can still
      // select-and-copy the text manually.
    }
  }

  return (
    <button
      type="button"
      aria-label="Copy API key"
      onClick={handleCopy}
      className="shrink-0 text-text-secondary hover:text-text-primary"
    >
      {copied ? <Check size={16} strokeWidth={1.5} className="text-success" /> : <Copy size={16} strokeWidth={1.5} />}
    </button>
  )
}

// Admin-panel eye toggle: masks the input as dots by default (like a real
// password field, via maskedTextStyle - not type="password", see above),
// reveals plain text on demand.
function EyeToggleButton({
  visible,
  onToggle,
}: {
  visible: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      aria-label={visible ? 'Hide API key' : 'Show API key'}
      onClick={onToggle}
      className="shrink-0 text-text-secondary hover:text-text-primary"
    >
      {visible ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
    </button>
  )
}

// The masked view's own "Show key location" toggle - an info/location icon,
// deliberately distinct from EyeToggleButton above: this never reveals a
// value that could be mistaken for the real key, only where it lives.
function LocationToggleButton({
  open,
  onToggle,
  loading,
}: {
  open: boolean
  onToggle: () => void
  loading?: boolean
}) {
  return (
    <button
      type="button"
      aria-label={open ? 'Hide key location' : 'Show key location'}
      onClick={onToggle}
      disabled={loading}
      className="shrink-0 text-text-secondary hover:text-text-primary disabled:cursor-wait"
    >
      {loading ? (
        <Loader2 size={16} strokeWidth={1.5} className="animate-spin" />
      ) : (
        <Info size={16} strokeWidth={1.5} />
      )}
    </button>
  )
}

// Masked placeholder shown by default for an existing firm, with two
// independent actions next to it: "Show key location" (read-only, displays
// where the real key lives - never the key itself) and "Replace key" (a
// distinct action: reveals an *empty* input for a genuinely new value -
// never a pre-filled masked string sitting in an editable text field where
// an accidental partial-edit could submit a mangled placeholder). PRD 06's
// own stated hard requirement.
export function MaskedApiKeyField({ value, onChange, isNewFirm, firmId, error }: MaskedApiKeyFieldProps) {
  const [replacing, setReplacing] = useState(false)
  const [visible, setVisible] = useState(false)
  const [locationOpen, setLocationOpen] = useState(false)

  // PRD 06's Locate Client API Key amendment: "useMutation (not useQuery -
  // deliberately never cached/refetched automatically, fired only by the
  // operator's own 'Show key location' click)". Its result lives only in
  // this mutation's own state, never written into the shared ['draft-file',
  // 'clients'] query cache every other part of this screen reads from.
  const locationMutation = useMutation({ mutationFn: () => getFirmApiKeyLocation(firmId) })

  function handleToggleLocation() {
    if (locationOpen) {
      setLocationOpen(false)
      return
    }
    if (locationMutation.data !== undefined) {
      setLocationOpen(true)
      return
    }
    locationMutation.mutate(undefined, { onSuccess: () => setLocationOpen(true) })
  }

  if (isNewFirm) {
    return (
      <div className="flex flex-col gap-1">
        <label className="text-meta font-medium text-text-secondary" htmlFor="firm-api-key">
          API key
        </label>
        <div className="flex w-full items-center gap-3">
          <input
            id="firm-api-key"
            type="text"
            autoComplete="off"
            data-lpignore="true"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            style={maskedTextStyle(!visible)}
            placeholder="Paste this firm's own API key"
            className={clsx(
              'w-full flex-1 rounded-control border bg-bg px-3 py-2 font-mono text-body text-text-primary outline-none placeholder:font-sans placeholder:text-text-disabled',
              error ? 'border-danger' : 'border-border',
            )}
          />
          <EyeToggleButton visible={visible} onToggle={() => setVisible((current) => !current)} />
          <CopyButton value={value} />
          <Tooltip text="Creates a random placeholder value. Most firms should paste in their own real API key instead.">
            <button
              type="button"
              onClick={() => onChange(generateApiKey())}
              className="shrink-0 text-meta font-medium text-accent hover:underline"
            >
              Generate
            </button>
          </Tooltip>
        </div>
        <span className="text-meta text-text-secondary">Enter this firm's own API key</span>
        {error && <span className="text-meta text-danger">{error}</span>}
      </div>
    )
  }

  if (!replacing) {
    const location = locationMutation.data
    const locationText =
      locationOpen && location !== undefined
        ? location.migrated
          ? `Stored in AWS Secrets Manager as ${location.secretRef}`
          : 'Not yet migrated — raw key in config'
        : undefined
    const displayValue = locationText ?? MASKED_API_KEY_PLACEHOLDER
    return (
      <div className="flex flex-col gap-1">
        <span className="text-meta font-medium text-text-secondary">API key</span>
        <div className="flex w-full items-center gap-3">
          <span
            className={clsx(
              'w-full flex-1 rounded-control border border-border bg-bg px-3 py-2 font-mono text-body',
              locationText !== undefined ? 'text-text-primary' : 'text-text-disabled',
            )}
          >
            {displayValue}
          </span>
          <LocationToggleButton open={locationOpen} onToggle={handleToggleLocation} loading={locationMutation.isPending} />
          <Tooltip text="Replacing this key stops the firm's old key from working right away, until they switch to the new one.">
            <button
              type="button"
              onClick={() => {
                // Show key location and Replace are mutually exclusive on
                // screen (PRD 06's Locate Client API Key amendment) -
                // opening Replace re-masks the display first, dropping the
                // cached location too rather than just hiding it, so
                // nothing lingers longer than needed.
                setLocationOpen(false)
                locationMutation.reset()
                setReplacing(true)
              }}
              className="shrink-0 text-meta font-medium text-accent hover:underline"
            >
              Replace key
            </button>
          </Tooltip>
        </div>
        {locationMutation.isError && <span className="text-meta text-danger">Couldn't load key location</span>}
        {error && <span className="text-meta text-danger">{error}</span>}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-1">
      <label className="text-meta font-medium text-text-secondary" htmlFor="firm-api-key">
        New API key
      </label>
      <div className="flex w-full items-center gap-3">
        <input
          id="firm-api-key"
          type="text"
          autoComplete="off"
          data-lpignore="true"
          autoFocus
          value={value === MASKED_API_KEY_PLACEHOLDER ? '' : value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={(event) => {
            // Abandoning the reveal without a genuinely typed value must
            // leave the field as the masked placeholder (submitted
            // unchanged) - never an accidental empty string.
            if (event.target.value.trim().length === 0) onChange(MASKED_API_KEY_PLACEHOLDER)
          }}
          style={maskedTextStyle(!visible)}
          placeholder="Paste the new API key"
          className="w-full flex-1 rounded-control border border-border bg-bg px-3 py-2 font-mono text-body text-text-primary outline-none placeholder:font-sans placeholder:text-text-disabled"
        />
        <EyeToggleButton visible={visible} onToggle={() => setVisible((current) => !current)} />
        <CopyButton value={value === MASKED_API_KEY_PLACEHOLDER ? '' : value} />
        <Tooltip text="Creates a random placeholder value. Most firms should paste in their own real API key instead.">
          <button
            type="button"
            onClick={() => onChange(generateApiKey())}
            className="shrink-0 text-meta font-medium text-accent hover:underline"
          >
            Generate
          </button>
        </Tooltip>
        <button
          type="button"
          onClick={() => {
            onChange(MASKED_API_KEY_PLACEHOLDER)
            setReplacing(false)
          }}
          className="shrink-0 text-meta font-medium text-text-secondary hover:text-text-primary"
        >
          Cancel
        </button>
      </div>
      {error && <span className="text-meta text-danger">{error}</span>}
    </div>
  )
}
